import { VendingReplenishmentTransactionVolumeHourDayHeatmapChart, hasChartSeries } from '@frontend/chart/components';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage } from 'react-intl';

import { useInsightHeatmapCellClick } from '../heatmap-cell-summary/use-insight-heatmap-cell-click';
import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { useInsightChartMinHeight } from '../insight-detail-mode.context';
import { InsightId } from '../insight-registry/insight-ids';
import { useLast2WeeksVendingReplenishmentActivityHourDayHeatmapChart } from './use-last-2-weeks-vending-replenishment-activity-hour-day-heatmap-chart';

export interface Last2WeeksVendingReplenishmentActivityHourDayHeatmapChartProps {
    accountId: string;
    width?: InsightCardWidth;
}

export const Last2WeeksVendingReplenishmentActivityHourDayHeatmapChart = ({
    accountId,
    width
}: Last2WeeksVendingReplenishmentActivityHourDayHeatmapChartProps) => {
    const chartMinHeight = useInsightChartMinHeight(280);
    const { data, isLoading, rangeStart, dayCount } = useLast2WeeksVendingReplenishmentActivityHourDayHeatmapChart(accountId);
    const title = (
        <FormattedMessage
            id='platform.analytics.insights.last-2-weeks-vending-replenishment-activity-hour-day-heatmap-chart.title'
            description='Title of the vending insights heatmap showing completed inbound replenishment transaction volume by hour of day over the last two weeks'
            defaultMessage='Replenishment heatmap'
        />
    );
    const onCellClick = useInsightHeatmapCellClick({
        accountId,
        chartKey: VendingReplenishmentTransactionVolumeHourDayHeatmapChart.definition.key,
        title
    });

    return (
        <InsightCard
            insightId={InsightId.VENDING_REPLENISHMENT_ACTIVITY_HOUR_DAY_HEATMAP}
            chartKey={VendingReplenishmentTransactionVolumeHourDayHeatmapChart.definition.key}
            width={width}
            title={title}
            subtitle={
                <FormattedMessage
                    id='platform.analytics.insights.last-2-weeks-vending-replenishment-activity-hour-day-heatmap-chart.subtitle'
                    description='Subtitle on the vending replenishment heatmap indicating the chart covers the last 14 days'
                    defaultMessage='last 14 days'
                />
            }
            isLoading={isLoading}>
            {!hasChartSeries(data) || !data ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : (
                <div
                    className='w-full min-w-0 overflow-hidden'
                    style={{ height: chartMinHeight }}>
                    <VendingReplenishmentTransactionVolumeHourDayHeatmapChart
                        data={data}
                        rangeStart={rangeStart}
                        dayCount={dayCount}
                        minHeight={chartMinHeight}
                        onCellClick={onCellClick}
                    />
                </div>
            )}
        </InsightCard>
    );
};
